import {useNavigate, useParams} from "react-router-dom";
import {useUserContext} from "../providers/UserContext";
import {Button} from "../components/common/Button";
import {useEffect, useState} from "react";
import {addChat, addMessage} from "../lib/database";
import { v4 as uuidv4 } from 'uuid';
import {Chat, Message, userOrGroup, WorkspaceMember} from "../lib/types";
import {Icon} from "../components/common/Icon";
import {useInboxContext} from "../providers/InboxProvider";
import {UserPicker} from "../components/common/UserPicker";
import {MessageComposer} from "../components/chat/MessageComposer";
import {Descendant} from "slate";
import {extractMentions, slateToPlainText} from "../components/chat/PreviewRenderer";
import {useChatListContext} from "../providers/ChatListProvider";
import {useLayoutContext} from "../providers/LayoutProvider";
import classNames from "classnames";
export const Compose = ({}: {}) => {
  const { groupId } = useParams();
  const [ to, setTo ] = useState<userOrGroup[]>([]);
  const { user, groups, activeWorkspace, setActiveChatId, getMember, sidebar} = useUserContext();
  const { currentFilter } = useChatListContext()
  const { setInboxVisible } = useInboxContext();
  const [loading, setLoading] = useState(false);
  const members = activeWorkspace?.members ? Object.values(activeWorkspace.members) : [];
  const [recipients, setRecipients] = useState<WorkspaceMember[]>([]);
  useEffect(() => {
    if (currentFilter?.startsWith("dm_")) {
      const id = currentFilter.replace("dm_", "");
      const member = getMember(id);
      if (member) {
        const newTo = [...to]
        newTo.push({
          type: "user",
          id: member.id,
          name: member.displayName,
          user: member,
        });
        setTo(newTo);
      }
    }
    if (currentFilter?.startsWith("group_")) {
      const id = currentFilter.replace("group_", "");
      const group = groups.find(group => group.id === id);
      if (group) {
        const newTo = [...to]
        newTo.push({
          type: "group",
          id: group.id,
          name: group.name,
          group,
        });
        setTo(newTo);
      }
    }
    if (currentFilter?.startsWith("sidebar_")) {
      const id = currentFilter.replace("sidebar_", "");
      const sidebarItem = sidebar.find(item => item.id === id);
      if (sidebarItem) {
        const newTo = [...to]
        sidebarItem.meaningfulGroups?.forEach(groupId => {
          const group = groups.find(group => group.id === groupId);
          if (group) {
            newTo.push({
              type: "group",
              id: group.id,
              name: group.name,
              group,
            });
          }
        });
        sidebarItem.meaningfulUsers?.forEach(userId => {
          if (userId === user?.uid) return;
          const member = getMember(userId);
          if (member) {
            newTo.push({
              type: "user",
              id: member.id,
              name: member.displayName,
              user: member,
            });
          }
        });
        setTo(newTo);
      }
    }
  }, [activeWorkspace, currentFilter]);
  useEffect(() => {
    setActiveChatId("");
  }, [setActiveChatId]);
  useEffect(() => {
    if (groupId) {
      const group = groups.find(group => group.id === groupId);
      if (group) {
        const newTo = [...to]
        newTo.push({
          type: "group",
          id: group.id,
          name: group.name,
          group,
        });
        setTo(newTo);
      }
    }
  }, [groupId]);
  const [content, setContent] = useState("");
  useEffect(() => {
    setRecipients(members.filter(member => {
      let listed = false;
      to.forEach(to => {
        if (to.type === "user" && to.id === member.id) {
          listed = true;
        }
        if (
          to.type === "group" &&
          (
            to.group?.permissions[member.id] !== undefined ||
            to.group?.visibility === 'public'
          )
        ) {
          listed = true;
        }
      });
      return listed;
    }))
  }, [to]);

  const navigate = useNavigate();

  const post = async (message: Descendant[]) => {
    if (loading) return;
    if (to.length === 0) return;
    if (!slateToPlainText(message)) return;
    setLoading(true);
    if (user && activeWorkspace) {
      setInboxVisible(true);
      const chatId = uuidv4();
      const groupPermissions: Record<string, 'write'> = {};
      const userPermissions: Record<string, 'write' | 'owner'> = {
        [user.uid]: 'owner',
      };
      to.forEach(recipient => {
        if (recipient.type === "group") {
          groupPermissions[recipient.id] = 'write';
        } else {
          userPermissions[recipient.id] = 'write';
        }
      });
      const fullMessage: Message = {
        id: uuidv4(),
        chatId: chatId,
        content: JSON.stringify(message),
        plainText: slateToPlainText(message),
        timestamp: Date.now(),
        visibility: 'public',
        authorId: user?.uid || "",
        authorName: user?.displayName || "",
        mentions: extractMentions(message),
      }
      const chat: Chat = {
        id: chatId,
        groupId: groupId || "",
        workspaceId: activeWorkspace.id,
        title: "Generating title...",
        summary: slateToPlainText(message),
        timestamp: Date.now(),
        authorId: user.uid,
        authorName: user.displayName || "",
        lastActiveAt: Date.now(),
        lastMessage: fullMessage,
        threads: {},
        userPermissions,
        groupPermissions,
        createdAt: Date.now(),
      }
      await addChat(chat)
      await addMessage(fullMessage, user, chat);
      navigate(`/chat/${chat.id}`);
      setLoading(false);
    }
  }
  const {isMobileResolution, setSidebarVisibility} = useLayoutContext();
  return (
      <div className="w-full h-full flex justify-left items-center">
        <div className="flex flex-col h-full flex-grow justify-between">
          <div className="border-b border-b-gray-6 p-4 flex items-center gap-2 mt-[1px]">
            {isMobileResolution && (
              <Button variant={"tertiary"} icon={"only"} onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate("/inbox");
                }
              }}>
                <Icon name={"close"}/>
              </Button>
            )}
            <span>New chat</span>
          </div>
          <div className="lg:p-4 flex flex-col flex-grow items-end justify-end lg:gap-3">
            <div className="users w-full">
              <UserPicker
                inputClass="border-0 shadow-elevation-1"
                label={""}
                allowGroups={true}
                showInlineSelected={true}
                selectedMembers={to}
                onRemove={(member: userOrGroup) => {
                  setTo(to.filter(m => m.id !== member.id));
                }}
                onSelect={(member: userOrGroup) => {
                setTo([...to, member]);
              }} />
            </div>
            <div className={classNames(
              "w-full",
              isMobileResolution && "flex-grow"
            )}>
              <MessageComposer
                disabled={recipients.length === 0}
                onSend={(e) => {
                post(e.children);
              }}
               minHeight={isMobileResolution ? "full" : 80}
                hiddenToolbar={false}
              />
            </div>
          </div>
        </div>
      </div>
  )
}